import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import type { IRes } from '../types/apis/base'
import { showToast } from 'vant'
import { useUserStore } from '@/stores/user'
import { getLocale } from '@/i18n'

// 通用请求配置（扩展 axios 配置）
interface HttpRequestConfig extends AxiosRequestConfig {
  transform?: boolean
}

type Locale = 'zh-CN' | 'en-US'

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]'
}

const getLocalizedStringValue = (baseValue: unknown, enValue: unknown, locale: Locale): string | undefined => {
  if (locale !== 'en-US') {
    return typeof baseValue === 'string' ? baseValue : undefined
  }

  if (typeof enValue === 'string') {
    const trimmed = enValue.trim()
    if (trimmed.length > 0) {
      return enValue
    }
  }

  return typeof baseValue === 'string' ? baseValue : undefined
}

const transformLocalizedFields = <T>(input: T, locale: Locale): T => {
  if (Array.isArray(input)) {
    return input.map(item => transformLocalizedFields(item, locale)) as T
  }

  if (!isPlainObject(input)) {
    return input
  }

  const output: Record<string, unknown> = {}

  Object.entries(input).forEach(([key, value]) => {
    output[key] = transformLocalizedFields(value, locale)
  })

  if (locale !== 'en-US') {
    return output as T
  }

  Object.entries(input).forEach(([key, value]) => {
    if (!key.endsWith('En')) {
      return
    }

    const baseKey = key.slice(0, -2)
    if (!baseKey || !(baseKey in output)) {
      return
    }

    const localized = getLocalizedStringValue((input as Record<string, unknown>)[baseKey], value, locale)
    if (localized !== undefined) {
      output[baseKey] = localized
    }
  })

  return output as T
}

const splitTransformConfig = (config?: HttpRequestConfig) => {
  const transform = config?.transform ?? true
  if (!config) {
    return { axiosConfig: undefined, transform }
  }

  const { transform: _transform, ...axiosConfig } = config
  return { axiosConfig, transform }
}

// 创建axios实例
const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 1000 * 60 * 5, // 5分钟超时，适用于长时间请求
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
http.interceptors.request.use(
  config => {
    // 从localStorage获取token
    const token = useUserStore().token
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse<IRes>) => {
    switch (response.data.ErrorCode) {
      case ErrorCode.SUCCESS:
        return response
      case ErrorCode.UNAUTHORIZED:
        // 未授权时仅通过 userStore 统一执行清理和跳转
        useUserStore().logout()
        throw new Error('UNAUTHORIZED')
      default:
        // 其他错误码，抛出错误
        const errorMsg = response.data.Msg || '请求失败'
        showToast({
          message: errorMsg,
          type: 'fail',
        })
        throw new Error(errorMsg)
    }
  },
  error => {
    // 统一错误处理
    if (error.response) {
      // 服务器返回错误
      const { status, data } = error.response
      console.error(`请求错误: ${status} - ${data?.Msg || '未知错误'}`)
    } else if (error.request) {
      // 请求已发送但没有收到响应
      console.error('网络错误: 无法连接到服务器')
    } else {
      // 请求配置错误
      console.error('请求错误:', error.message)
    }
    return Promise.reject(error)
  },
)

// 通用GET方法
export const get = async <T>(url: string, config?: HttpRequestConfig): Promise<IRes<T>> => {
  const { axiosConfig, transform } = splitTransformConfig(config)
  const response = await http.get<IRes<T>>(url, axiosConfig)

  if (!transform) {
    return response.data
  }

  const locale = getLocale()
  return {
    ...response.data,
    Body: transformLocalizedFields(response.data.Body, locale),
  }
}

// 通用POST方法
export const post = async <T>(url: string, data?: any, config?: HttpRequestConfig): Promise<IRes<T>> => {
  const { axiosConfig, transform } = splitTransformConfig(config)
  const response = await http.post<IRes<T>>(url, data, axiosConfig)

  if (!transform) {
    return response.data
  }

  const locale = getLocale()
  return {
    ...response.data,
    Body: transformLocalizedFields(response.data.Body, locale),
  }
}

export default http

// 错误码枚举
export enum ErrorCode {
  SUCCESS = 0,
  FAILED = 1,
  SERVER_ERROR = 500,
  UNAUTHORIZED = 3,
}
